// S7 — camera-layouts API client + react-query hooks.
//
// Used by LayoutManager (CRUD) and LiveView (load + save current layout).
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { api } from "./client";

export type GridSize =
  | "1x1"
  | "1x2"
  | "2x2"
  | "2x3"
  | "3x3"
  | "3x4"
  | "4x4";

export const GRID_SIZE_OPTIONS: GridSize[] = [
  "1x1",
  "1x2",
  "2x2",
  "2x3",
  "3x3",
  "3x4",
  "4x4",
];

// The backend's format_timestamp shape — see frontend/src/utils/datetime.ts
type Ts = { utc: string; local: string; timezone: string };

export interface LayoutCell {
  position: number;
  camera_id: string | null;
  label: string;
}

export interface LayoutConfig {
  cells: LayoutCell[];
}

export interface LayoutListItem {
  id: string;
  name: string;
  grid_size: GridSize;
  entity_id: string | null;
  entity_name: string | null;
  is_default: boolean;
  camera_count: number;
  created_at: Ts | null;
  updated_at: Ts | null;
}

export interface LayoutDetail extends LayoutListItem {
  layout_config: LayoutConfig;
}

export interface LayoutCreateInput {
  name: string;
  grid_size: GridSize;
  layout_config: LayoutConfig;
  entity_id?: string | null;
  is_default?: boolean;
}

export interface LayoutUpdateInput {
  name?: string;
  grid_size?: GridSize;
  layout_config?: LayoutConfig;
  is_default?: boolean;
}

const layoutsKey = ["layouts"] as const;

export function useLayouts() {
  return useQuery({
    queryKey: [...layoutsKey, "list"],
    queryFn: async () => {
      const { data } = await api.get<LayoutListItem[]>("/api/v1/layouts");
      return data;
    },
  });
}

export function useLayout(id: string | null) {
  return useQuery({
    queryKey: [...layoutsKey, "detail", id],
    queryFn: async () => {
      const { data } = await api.get<LayoutDetail>(`/api/v1/layouts/${id}`);
      return data;
    },
    enabled: Boolean(id),
  });
}

export function useDefaultLayout(enabled = true) {
  return useQuery({
    queryKey: [...layoutsKey, "default"],
    queryFn: async () => {
      const { data } = await api.get<LayoutDetail | null>(
        "/api/v1/layouts/default",
      );
      return data;
    },
    enabled,
  });
}

export function useCreateLayout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: LayoutCreateInput) => {
      const { data } = await api.post<LayoutDetail>("/api/v1/layouts", input);
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: layoutsKey });
    },
  });
}

export function useUpdateLayout(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: LayoutUpdateInput) => {
      const { data } = await api.put<LayoutDetail>(
        `/api/v1/layouts/${id}`,
        patch,
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: layoutsKey });
    },
  });
}

export function useDeleteLayout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/v1/layouts/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: layoutsKey });
    },
  });
}
